import React, { useState } from 'react';

import { RcFile } from 'antd/lib/upload';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { UploadFile } from 'antd/lib/upload/interface';
import { Avatar, message, Typography, Upload } from 'antd';
import ImgCrop from 'antd-img-crop';

import { useClient } from 'src/contexts';
import { MediaApiResponseType } from 'src/types';

import styles from './ImageCropUploader.module.css';
import { UploadedMediaType } from '../MediaUploader/types';
import { checkFileFormat, fileToDataUri } from './utils';

const { Text } = Typography;

type ImageCropUploaderProps = {
  defaultValue?: Array<UploadFile>;
  id?: string;
  onUpload: (mediaIdList: Array<UploadedMediaType>) => void;
  pictureListType: 'picture-card' | 'picture';
  placeholder?: string;
  aspect?: number;
  cropShape?: 'rect' | 'round';
};

type ImageCropType = {
  uid: string;
  url?: string;
};

type ImageType = Array<ImageCropType> | null;

export const ImageCropUploader = ({
  id,
  onUpload,
  pictureListType,
  placeholder,
  defaultValue,
  aspect = 1,
  cropShape = 'round',
}: ImageCropUploaderProps) => {
  const [image, setImage] = useState<string | null>();
  const [canRemove, setCanRemove] = useState<boolean>(false);
  const [imageValue, setImageValue] = useState<ImageType>(defaultValue || null);

  const { get, upload, deletion } = useClient();

  const handleUpload = (file: RcFile, dataUri: string) => {
    upload(
      { name: file.name, file: dataUri },
      (mediaId: string) => {
        get(
          `/medias/${mediaId}`,
          (media: { data: MediaApiResponseType & { id: string } }) => {
            media.data &&
              onUpload([
                {
                  id: media.data.id,
                  name: media.data.name,
                  type: media.data.mime,
                  url: media.data.url.original,
                },
              ]);
          }
        );
      },
      () => message.error('Une erreur est survenue lors du téléchargement')
    );
  };

  const beforeUpload = (file: RcFile) => {
    if (checkFileFormat(file)) {
      fileToDataUri(file).then((dataUri: any) => {
        setImage(dataUri);
        handleUpload(file, dataUri);
      });
    }
    return false;
  };

  const handleRemove = (e: any, mediaId: string) => {
    e.stopPropagation();
    if (imageValue && imageValue[0]) {
      deletion(
        `/medias/${mediaId}`,
        {},
        () => {
          setCanRemove(true);
          setImageValue(null);
          onUpload([]);
        },
        () => {
          message.error(
            'Une erreur est survenue lors de suppression de la photo'
          );
        }
      );
    } else {
      setImage(null);
      onUpload([]);
    }
  };

  const renderFilePreview = (imageUri: string) => (
    <div className={`${styles.mediaContainer} ${styles.dash}`}>
      <Avatar
        size={80}
        shape={cropShape === 'rect' ? 'square' : 'circle'}
        src={imageUri}
        className={styles.image}
      />
      <DeleteOutlined
        id="remove-icon"
        className={styles.removeIcon}
        onClick={e => handleRemove(e, '')}
      />
    </div>
  );

  return (
    <div className={styles.uploadeContainer}>
      {placeholder && pictureListType === 'picture-card' && (
        <Text className={styles.placeholder} strong={true}>
          {placeholder}
        </Text>
      )}
      {image ? (
        renderFilePreview(image)
      ) : (
        <ImgCrop shape={cropShape} aspect={aspect} rotate={true}>
          <Upload
            action={'https://www.mocky.io/v2/5cc8019d300000980a055e76'}
            beforeUpload={beforeUpload}
            data-testid="image-uploader"
            id={id || 'image-uploader'}
            listType={pictureListType}
            showUploadList={false}
          >
            {!canRemove && imageValue && imageValue[0] ? (
              <div className={styles.mediaContainer}>
                <Avatar
                  size={80}
                  shape={cropShape === 'rect' ? 'square' : 'circle'}
                  src={imageValue[0].url}
                  className={styles.image}
                />
                <DeleteOutlined
                  id="remove-icon"
                  className={styles.removeIcon}
                  onClick={e => handleRemove(e, imageValue[0].uid)}
                />
              </div>
            ) : (
              <PlusOutlined id={`${id}-plus-icon`} />
            )}
          </Upload>
        </ImgCrop>
      )}
    </div>
  );
};
